// filepath: apps/web/src/features/lists/hooks/useEntityListFilter.ts
//
// FR-UI-LIST-1 AC-2 — URL-state hook for the list view's filter + sort.
//
// Manages two URL query parameters:
//   - `filter` — URL-encoded JSON array of {field, op, value}
//   - `sort`   — comma-separated `field:dir` pairs
//
// The hook is the SPA-side mirror of FR-QRY-2 AC-3 (the server-side
// filterable-fields boundary). The allowlist is computed from the
// resolved entity definition:
//
//   columnFields        = fields.filter(f => f.stored === "column").map(f => f.name)
//   declaredJSONBKeys   = definition.filterable_jsonb_keys ?? []
//   allowlist           = new Set([...columnFields, ...declaredJSONBKeys])
//
// Any `addFilter(key, ...)` call for a `key` outside the allowlist
// is a SILENT NO-OP. This is the SPA mirror of the server's HTTP 400
// on undeclared JSONB keys (FR-QRY-2 AC-3) — defense-in-depth.
// The server remains the authoritative boundary; the SPA mirror
// prevents the round-trip in the first place.
//
// State ownership: filter + sort live as React state inside the
// hook. On mount the hook reads them from the URL (re-validating
// every entry against the allowlist). Every mutator writes BOTH
// the React state AND the URL via `replaceState`, so a subsequent
// mount (or another consumer reading the URL) sees the same
// values. In tests, `locationOverride` + `historyOverride` are
// fakes; the hook writes to the fake history but the fake
// location's `.search` does NOT auto-update — this is fine
// because the hook's source of truth is its own React state.
//
// AC-3 invariant (keyset filter+sort survives cursor advances): the
// filter + sort state lives in URL query params + hook state, NOT
// in the cursor. The next-page query uses the same filter+sort
// with a fresh cursor.

import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  EntityDefinitionWire,
  EntityListFilter,
  EntityListSort,
} from "../types";

export interface UseEntityListFilterOptions {
  /** Resolved entity definition (drives the allowlist). */
  definition: EntityDefinitionWire | null | undefined;
  /**
   * When false, the hook skips URL sync (e.g. in tests where
   * window.location is read-only or non-existent). The hook still
   * computes the allowlist and exposes the mutators.
   */
  syncToUrl?: boolean;
  /** Override `window.location` for testing. */
  locationOverride?: Location | null;
  /**
   * Override `window.history` for testing (pushState / replaceState).
   * Defaults to `window.history` when syncToUrl is true.
   */
  historyOverride?: History | null;
}

export interface UseEntityListFilterResult {
  /** Current active filter expressions (allowlist-validated). */
  filter: EntityListFilter[];
  /** Current active sort expressions (allowlist-validated). */
  sort: EntityListSort[];
  /** The allowlist as a Set for O(1) membership checks. */
  allowlist: Set<string>;
  /** True when the hook has read filter/sort from the URL on mount. */
  hasReadFromUrl: boolean;
  /**
   * Add a filter expression. Silent no-op when `key` is outside
   * the allowlist (AC-2 invariant — SPA mirror of FR-QRY-2 AC-3).
   */
  addFilter: (
    key: string,
    op: EntityListFilter["op"],
    value: EntityListFilter["value"],
  ) => void;
  /**
   * Remove a filter entry by `field` name. Silent no-op when no
   * filter entry exists for the given field.
   */
  clearFilter: (key: string) => void;
  /**
   * Set a sort expression for `field`. Replaces any existing sort
   * for the same field. Silent no-op when `field` is outside the
   * allowlist (AC-2 mirror).
   */
  setSort: (field: string, dir: EntityListSort["dir"]) => void;
  /**
   * Cycle the sort for `field`: undefined → ASC → DESC → undefined.
   * Silent no-op when `field` is outside the allowlist (AC-2).
   */
  toggleSort: (field: string) => void;
  /** Reset filter + sort to empty (URL is updated when syncToUrl). */
  reset: () => void;
}

/**
 * Compute the allowlist from the entity definition.
 *
 * Mirrors FR-QRY-2 AC-2 / AC-3 server-side validation: only
 * column-stored fields AND JSONB keys explicitly declared as
 * filterable are accepted.
 */
export function computeAllowlist(
  definition: EntityDefinitionWire | null | undefined,
): Set<string> {
  const out = new Set<string>();
  if (definition === null || definition === undefined) return out;
  const defJson = definition.definition_json;
  if (defJson === undefined || defJson === null) return out;
  // (a) column-stored fields
  for (const f of defJson.fields ?? []) {
    if (f.stored === "column") out.add(f.name);
  }
  // (b) declared filterable JSONB keys
  for (const k of defJson.filterable_jsonb_keys ?? []) {
    out.add(k);
  }
  return out;
}

/**
 * Parse the `filter` URL param into an EntityListFilter[].
 * Tolerant of malformed JSON (returns [] on parse failure).
 */
function parseFilterParam(raw: string | null): EntityListFilter[] {
  if (raw === null || raw === "") return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: EntityListFilter[] = [];
  for (const entry of parsed) {
    if (!isFilterEntry(entry)) continue;
    out.push({
      field: String(entry.field),
      op: entry.op,
      value: normaliseFilterValue(entry.value),
    });
  }
  return out;
}

function isFilterEntry(value: unknown): value is EntityListFilter {
  if (value === null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.field === "string" &&
    typeof v.op === "string" &&
    ["eq", "neq", "contains", "gt", "gte", "lt", "lte"].includes(v.op)
  );
}

function normaliseFilterValue(
  value: unknown,
): string | number | boolean | null {
  if (value === null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value;
  return null;
}

/**
 * Serialise filter back into the `filter` URL param string.
 * Returns `""` when no filters are active (URL omits the param).
 */
function serialiseFilter(filter: EntityListFilter[]): string {
  if (filter.length === 0) return "";
  return encodeURIComponent(JSON.stringify(filter));
}

/**
 * Parse the `sort` URL param into an EntityListSort[].
 * Format: comma-separated `field:dir` pairs (server-side canonical).
 */
function parseSortParam(raw: string | null): EntityListSort[] {
  if (raw === null || raw === "") return [];
  const out: EntityListSort[] = [];
  for (const part of raw.split(",")) {
    const trimmed = part.trim();
    if (trimmed === "") continue;
    const lastColon = trimmed.lastIndexOf(":");
    if (lastColon <= 0) continue;
    const field = trimmed.slice(0, lastColon);
    const dirRaw = trimmed.slice(lastColon + 1).toLowerCase();
    if (dirRaw !== "asc" && dirRaw !== "desc") continue;
    if (field === "") continue;
    out.push({ field, dir: dirRaw });
  }
  return out;
}

/**
 * Serialise sort back into the `sort` URL param string.
 * Returns `""` when no sorts are active (URL omits the param).
 */
function serialiseSort(sort: EntityListSort[]): string {
  if (sort.length === 0) return "";
  return sort.map((s) => `${s.field}:${s.dir}`).join(",");
}

/**
 * Validate a filter + sort pair against the allowlist, dropping
 * entries whose field is outside the allowlist.
 */
function validateAgainstAllowlist(
  filter: EntityListFilter[],
  sort: EntityListSort[],
  allowlist: Set<string>,
): { filter: EntityListFilter[]; sort: EntityListSort[] } {
  return {
    filter: filter.filter((f) => allowlist.has(f.field)),
    sort: sort.filter((s) => allowlist.has(s.field)),
  };
}

/**
 * useEntityListFilter — URL-state hook for filter + sort.
 *
 * The hook is the SPA mirror of FR-QRY-2 AC-3 — see file header.
 */
export function useEntityListFilter(
  options: UseEntityListFilterOptions,
): UseEntityListFilterResult {
  const {
    definition,
    syncToUrl = true,
    locationOverride = null,
    historyOverride = null,
  } = options;

  const allowlist = useMemo(() => computeAllowlist(definition), [definition]);

  // Read the current URL state. When `syncToUrl` is false the hook
  // treats URL state as empty (test mode).
  const loc =
    locationOverride ??
    (typeof window !== "undefined" ? window.location : null);
  const hist =
    historyOverride ?? (typeof window !== "undefined" ? window.history : null);

  // Initial state read from the URL (re-validated against the
  // allowlist). The hook's source of truth is its OWN state — the
  // URL is just an external sync target.
  const initial = useMemo<{
    filter: EntityListFilter[];
    sort: EntityListSort[];
  }>(() => {
    if (loc === null) return { filter: [], sort: [] };
    const params = new URLSearchParams(loc.search);
    const rawFilter = parseFilterParam(params.get("filter"));
    const rawSort = parseSortParam(params.get("sort"));
    return validateAgainstAllowlist(rawFilter, rawSort, allowlist);
    // We deliberately do NOT depend on `allowlist` here — see the
    // useEffect below that handles the post-mount allowlist change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React state — the source of truth after mount. Mutators write
  // BOTH this state and the URL.
  const [filter, setFilterState] = useState<EntityListFilter[]>(initial.filter);
  const [sort, setSortState] = useState<EntityListSort[]>(initial.sort);

  // When the allowlist changes after the definition fetch resolves,
  // re-validate the current filter + sort and drop any entries
  // outside the new allowlist. This is the URL-re-hydration path:
  // the user may have hand-edited the URL with an undeclared key;
  // the allowlist shrinks → we drop the now-invalid entry.
  useEffect(() => {
    setFilterState((prev) => prev.filter((f) => allowlist.has(f.field)));
    setSortState((prev) => prev.filter((s) => allowlist.has(s.field)));
  }, [allowlist]);

  const writeUrl = useCallback(
    (nextFilter: EntityListFilter[], nextSort: EntityListSort[]) => {
      if (!syncToUrl) return;
      if (loc === null || hist === null) return;
      const params = new URLSearchParams(loc.search);
      const filterStr = serialiseFilter(nextFilter);
      const sortStr = serialiseSort(nextSort);
      if (filterStr === "") {
        params.delete("filter");
      } else {
        params.set("filter", filterStr);
      }
      if (sortStr === "") {
        params.delete("sort");
      } else {
        params.set("sort", sortStr);
      }
      const query = params.toString();
      const nextUrl =
        loc.pathname + (query.length > 0 ? `?${query}` : "") + loc.hash;
      // Use replaceState — the back button should step between
      // pages, not between filter states (the cursor is in the
      // record payload, not in the URL; advancing the cursor
      // writes a new history entry naturally).
      hist.replaceState(null, "", nextUrl);
    },
    [syncToUrl, loc, hist],
  );

  const addFilter = useCallback<UseEntityListFilterResult["addFilter"]>(
    (key, op, value) => {
      // AC-2 invariant — SPA mirror of FR-QRY-2 AC-3. Silent no-op
      // for keys outside the allowlist. The server still rejects
      // undeclared JSONB keys with HTTP 400; this hook prevents the
      // round-trip in the first place (defense-in-depth).
      if (!allowlist.has(key)) return;
      const next = filter.filter((f) => f.field !== key);
      next.push({ field: key, op, value });
      setFilterState(next);
      writeUrl(next, sort);
    },
    [allowlist, filter, sort, writeUrl],
  );

  const clearFilter = useCallback<UseEntityListFilterResult["clearFilter"]>(
    (key) => {
      const next = filter.filter((f) => f.field !== key);
      if (next.length === filter.length) return;
      setFilterState(next);
      writeUrl(next, sort);
    },
    [filter, sort, writeUrl],
  );

  const setSort = useCallback<UseEntityListFilterResult["setSort"]>(
    (field, dir) => {
      if (!allowlist.has(field)) return;
      const next = sort.filter((s) => s.field !== field);
      next.push({ field, dir });
      setSortState(next);
      writeUrl(filter, next);
    },
    [allowlist, sort, filter, writeUrl],
  );

  const toggleSort = useCallback<UseEntityListFilterResult["toggleSort"]>(
    (field) => {
      if (!allowlist.has(field)) return;
      const existing = sort.find((s) => s.field === field);
      const next = sort.filter((s) => s.field !== field);
      if (existing === undefined) {
        next.push({ field, dir: "asc" });
      } else if (existing.dir === "asc") {
        next.push({ field, dir: "desc" });
      } else {
        // DESC → undefined (no sort entry for this field).
      }
      setSortState(next);
      writeUrl(filter, next);
    },
    [allowlist, sort, filter, writeUrl],
  );

  const reset = useCallback(() => {
    setFilterState([]);
    setSortState([]);
    writeUrl([], []);
  }, [writeUrl]);

  return {
    filter,
    sort,
    allowlist,
    hasReadFromUrl: true,
    addFilter,
    clearFilter,
    setSort,
    toggleSort,
    reset,
  };
}
